<template>
  <div class="message-center-container">
    <el-card class="message-card" shadow="never">
      <template #header>
        <div class="card-header">
          <div class="header-left">
            <el-button text @click="router.back()">返回</el-button>
            <h2 class="page-title">{{ pageTitle }}</h2>
          </div>
        </div>
      </template>

      <!-- 筛选栏 + 分类内“全部已读” -->
      <div class="filter-bar">
        <div class="filter-tabs">
          <div
            v-for="tab in filterTabs"
            :key="tab.value"
            :class="['filter-tab', { active: activeFilter === tab.value }]"
            @click="handleFilterChange(tab.value)"
          >
            {{ tab.label }}
          </div>
        </div>
        <el-button
          type="primary"
          :icon="Delete"
          :disabled="currentUnreadCount === 0"
          @click="handleMarkAllRead"
        >
          全部已读
        </el-button>
      </div>

      <!-- 消息列表 -->
      <div class="message-list-container">
        <el-scrollbar height="600px">
          <div v-if="loading && messageList.length === 0" class="loading-wrapper">
            <el-icon class="is-loading" :size="32"><Loading /></el-icon>
            <p>加载中...</p>
          </div>

          <div v-else-if="messageList.length === 0" class="empty-wrapper">
            <el-empty description="暂无消息" />
          </div>

          <div v-else class="message-list">
            <div
              v-for="message in messageList"
              :key="message.id"
              :class="['message-item', { unread: !message.isRead }]"
              @click="handleViewDetail(message)"
            >
              <div class="message-header">
                <div class="message-title-row">
                  <span class="message-title">{{ message.title }}</span>
                  <el-badge v-if="!message.isRead" :value="''" class="unread-badge" />
                </div>
                <span class="message-time">{{ formatTime(message.createTime) }}</span>
              </div>
              <div class="message-content">{{ message.content }}</div>
              <div v-if="message.relatedType && message.relatedId" class="message-footer">
                <el-tag size="small" type="info">{{ getTypeText(message.type, message.relatedType) }}</el-tag>
                <el-button type="primary" size="small" text @click.stop="handleRelatedAction(message)">
                  查看详情
                </el-button>
              </div>
            </div>
          </div>

          <!-- 加载更多 -->
          <div v-if="hasMore && messageList.length > 0" class="load-more">
            <el-button :loading="loading" text @click="loadMore">加载更多</el-button>
          </div>
          <div v-if="!hasMore && messageList.length > 0" class="no-more">
            <span>没有更多了</span>
          </div>
        </el-scrollbar>
      </div>
    </el-card>

    <!-- 消息关联详情弹窗 -->
    <el-dialog
      v-model="detailDialogVisible"
      :title="getDialogTitle()"
      width="70%"
      destroy-on-close
    >
      <div v-loading="detailLoading" style="min-height: 200px">
        <!-- 跟进详情（无审批） -->
        <template v-if="detailBusinessType === 'FOLLOW'">
          <el-descriptions v-if="detailFollow" :column="1" border>
            <el-descriptions-item label="客户">{{ detailFollow.customerName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="跟进方式">{{ getActivityTypeName(detailFollow.activityType || detailFollow.followMethod || detailFollow.followType) }}</el-descriptions-item>
            <el-descriptions-item label="跟进内容">{{ detailFollow.activityContent || detailFollow.content || detailFollow.followContent || '-' }}</el-descriptions-item>
            <el-descriptions-item label="下次跟进日期">{{ formatDateTime(detailFollow.nextFollowTime) || '-' }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ formatDateTime(detailFollow.createTime) || '-' }}</el-descriptions-item>
          </el-descriptions>
          <el-empty v-else description="未找到跟进详情" />
        </template>

        <!-- 其他业务详情 + 审批记录 -->
        <template v-else>
          <BusinessDetail
            v-if="detailBusinessType && detailBusinessId"
            :business-type="detailBusinessType"
            :business-id="detailBusinessId"
          />
          <el-empty v-else description="暂无详情" />

          <div v-if="approvalRecords.length" style="margin-top: 16px">
            <h4 style="margin-bottom: 8px">审批记录</h4>
            <ApprovalTimeline :records="approvalRecords" />
          </div>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Loading, Delete } from '@element-plus/icons-vue'
import { getMessageList, markMessageAsRead, markAllUnreadAsRead } from '../api/message'
import { getApprovalRecords, getFollowDetail } from '../api/salesman'
import BusinessDetail from '@/components/BusinessDetail.vue'
import ApprovalTimeline from '@/components/ApprovalTimeline.vue'

const route = useRoute()
const router = useRouter()

const pageTitle = computed(() => route.query.title || '消息明细')
const categoryKey = computed(() => route.query.categoryKey || '')

const filterTabs = [
  { label: '全部', value: 'all' },
  { label: '未读', value: 'unread' },
  { label: '已读', value: 'read' }
]

const activeFilter = ref('all')
const messageList = ref([])
const page = ref(1)
const pageSize = ref(20)
const hasMore = ref(true)
const loading = ref(false)

const loadMessageList = async (reset = false) => {
  if (loading.value) return
  if (!categoryKey.value) {
    messageList.value = []
    hasMore.value = false
    return
  }

  if (reset) {
    page.value = 1
    messageList.value = []
    hasMore.value = true
  }

  loading.value = true
  try {
    const params = {
      page: page.value,
      pageSize: pageSize.value,
      categoryKey: categoryKey.value
    }

    if (activeFilter.value === 'unread') params.isRead = false
    if (activeFilter.value === 'read') params.isRead = true

    const res = await getMessageList(params)
    if (res.code === '000000' && Array.isArray(res.data)) {
      const list = res.data
      if (reset) messageList.value = list
      else messageList.value = [...messageList.value, ...list]
      hasMore.value = list.length >= pageSize.value
    } else {
      throw new Error(res.msg || '加载失败')
    }
  } catch (e) {
    console.error('加载消息明细失败:', e)
    ElMessage.error(e.message || '加载失败')
    if (reset) messageList.value = []
  } finally {
    loading.value = false
  }
}

// 当前列表内未读数量
const currentUnreadCount = computed(() =>
  messageList.value.reduce((sum, item) => sum + (!item.isRead ? 1 : 0), 0)
)

const handleFilterChange = (value) => {
  if (activeFilter.value === value) return
  activeFilter.value = value
  loadMessageList(true)
}

const formatTime = (timeStr) => {
  if (!timeStr) return ''
  const date = new Date(timeStr)
  if (isNaN(date.getTime())) return ''

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diff = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24))

  if (diff === 0) {
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `今天 ${hours}:${minutes}`
  } else if (diff === 1) {
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `昨天 ${hours}:${minutes}`
  } else if (diff < 7) {
    const weekdays = ['日', '一', '二', '三', '四', '五', '六']
    return `星期${weekdays[date.getDay()]}`
  } else {
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${month}月${day}日`
  }
}

const getTypeText = (type, relatedType) => {
  if (type === 'approval_result' && relatedType) {
    const relatedTypeMap = {
      contract: '合同',
      return_order: '返货单',
      travel_expense: '报销申请',
      business_trip: '出差申请',
      customer_deal_record: '成交台账'
    }
    return relatedTypeMap[relatedType] || '审批结果'
  }
  const typeMap = {
    follow_reminder: '跟进提醒',
    contract_approval: '合同审批',
    system_notice: '系统通知',
    approval_result: '审批结果'
  }
  return typeMap[type] || '消息'
}

const handleViewDetail = async (message) => {
  if (!message?.id) return
  try {
    if (!message.isRead) {
      await markMessageAsRead(message.id)
      const index = messageList.value.findIndex((item) => item.id === message.id)
      if (index !== -1) messageList.value[index].isRead = true
      window.dispatchEvent(new CustomEvent('refresh-unread-count'))
    }
    if (message.relatedType && message.relatedId) handleRelatedAction(message)
  } catch (e) {
    console.error('标记已读失败:', e)
    if (message.relatedType && message.relatedId) handleRelatedAction(message)
  }
}

const handleRelatedAction = (message) => {
  openDetailDialog(message)
}

// ===== 详情弹窗 =====
const detailDialogVisible = ref(false)
const detailLoading = ref(false)
const detailBusinessType = ref('')
const detailBusinessId = ref(null)
const detailFollow = ref(null)
const approvalRecords = ref([])

const businessTypeMap = {
  contract: 'CONTRACT',
  return_order: 'RETURN_ORDER',
  travel_expense: 'TRAVEL_EXPENSE',
  business_trip: 'BUSINESS_TRIP',
  customer_deal_record: 'CUSTOMER_DEAL_RECORD',
  follow: 'FOLLOW',
  follow_record: 'FOLLOW'
}

const needApprovalTypes = ['CONTRACT', 'RETURN_ORDER', 'TRAVEL_EXPENSE', 'BUSINESS_TRIP', 'CUSTOMER_DEAL_RECORD']

const openDetailDialog = async (message) => {
  if (!message || !message.relatedType || !message.relatedId) {
    ElMessage.info('暂无关联详情')
    return
  }
  detailDialogVisible.value = true
  detailLoading.value = true
  detailBusinessType.value = businessTypeMap[String(message.relatedType).toLowerCase()] || ''
  detailBusinessId.value = message.relatedId
  detailFollow.value = null
  approvalRecords.value = []

  try {
    if (detailBusinessType.value === 'FOLLOW') {
      const res = await getFollowDetail(message.relatedId)
      if (res.code === '000000') detailFollow.value = res.data || {}
      else ElMessage.error(res.msg || '获取跟进详情失败')
      return
    }
    if (needApprovalTypes.includes(detailBusinessType.value)) {
      const res = await getApprovalRecords(detailBusinessType.value, message.relatedId)
      if (res.code === '000000' && Array.isArray(res.data)) approvalRecords.value = res.data
    }
  } catch (e) {
    console.error('加载消息关联详情失败:', e)
    ElMessage.error(e.message || '加载详情失败')
  } finally {
    detailLoading.value = false
  }
}

const getDialogTitle = () => {
  if (detailBusinessType.value === 'FOLLOW') return '跟进记录详情'
  const typeText = getTypeText('approval_result', (detailBusinessType.value || '').toLowerCase())
  return detailBusinessType.value ? `${typeText}详情` : '消息详情'
}

const formatDateTime = (dateTime) => {
  if (!dateTime) return ''
  try {
    const date = new Date(dateTime)
    if (Number.isNaN(date.getTime())) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  } catch {
    return ''
  }
}

const getActivityTypeName = (type) => {
  if (!type) return '-'
  const map = { visit: '拜访', phone: '电话', wechat: '微信', email: '邮件', meeting: '会议', other: '其他' }
  return map[String(type).toLowerCase()] || type || '-'
}

const loadMore = () => {
  if (!hasMore.value || loading.value) return
  page.value++
  loadMessageList(false)
}

// 分类内“全部已读”——目前后端只支持整体全部已读，这里沿用相同行为
const handleMarkAllRead = async () => {
  if (currentUnreadCount.value === 0) {
    ElMessage.info('没有未读消息')
    return
  }
  try {
    await ElMessageBox.confirm('确定要将所有未读消息标记为已读吗？', '确认操作', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await markAllUnreadAsRead()
    ElMessage.success('已全部标记为已读')
    await loadMessageList(true)
    window.dispatchEvent(new CustomEvent('refresh-unread-count'))
  } catch (e) {
    if (e !== 'cancel') {
      console.error('批量标记已读失败:', e)
      ElMessage.error(e.message || '操作失败')
    }
  }
}

onMounted(() => {
  loadMessageList(true)
})
</script>

<style scoped>
.message-center-container {
  padding: 20px;
  height: 100%;
}

.message-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.message-card :deep(.el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e4e7ed;
}

.filter-tabs {
  display: flex;
  gap: 8px;
}

.filter-tab {
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  color: #606266;
  background-color: #f5f7fa;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.filter-tab:hover {
  background-color: #e4e7ed;
  color: #409eff;
}

.filter-tab.active {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.message-list-container {
  flex: 1;
  overflow: hidden;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-item {
  padding: 20px;
  border-radius: 10px;
  background-color: #fff;
  border: 1px solid #e4e7ed;
  cursor: pointer;
  transition: all 0.3s ease;
}

.message-item:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.message-item.unread {
  border-left: 4px solid #409eff;
  background: linear-gradient(to right, #ecf5ff 0%, #fff 6%);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.message-title-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.unread-badge :deep(.el-badge__content) {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: #f56c6c;
}

.message-time {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  margin-left: 12px;
}

.message-content {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  margin-bottom: 12px;
}

.message-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px dashed #e4e7ed;
}

.loading-wrapper,
.empty-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #909399;
}

.load-more,
.no-more {
  text-align: center;
  padding: 20px;
  color: #909399;
  font-size: 14px;
}
</style>

